import { useEffect, useRef, useState } from "react";

/**
 * Reveals an element once it enters the viewport.
 * Uses IntersectionObserver only - no scroll listeners.
 *
 * Returns [ref, inView]. Elements stay visible after the first pass so
 * content never flickers back out while scrolling up.
 */
const useInView = ({ threshold = 0.15, rootMargin = "0px 0px -8% 0px" } = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || inView) return undefined;

    // Server render, old browsers or tests: show the content immediately.
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [inView, rootMargin, threshold]);

  return [ref, inView];
};

export default useInView;
